// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { scrollToFirstFormError } from "@insite/client-framework/Common/CustomerSelectors";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddressBookOverlayIsOpen from "@insite/client-framework/Store/Components/AddressBookOverlay/Handlers/SetAddressBookOverlayIsOpen";
import setStorePickupOverlayIsOpen from "@insite/client-framework/Store/Components/StorePickupOverlay/Handlers/SetStorePickupOverlayIsOpen";
import { getCurrentUserIsGuest, getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTo";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTo";
import loadShipTos from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTos";
import { getShipTosDataView, getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import setUseOneTimeAddress from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/SetUseOneTimeAddress";
import resetShippingAddressFormValidation from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/ResetShippingAddressFormValidation";
import saveShipping from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SaveShipping";
import setIsBillingAddressTheSameAsShipping from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetIsBillingAddressTheSameAsShipping";
import setPaymentDetailsState from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetPaymentDetailsState";
import translate from "@insite/client-framework/Translate";
import { ShipToModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import { OnePageCheckoutFormContext } from "@insite/content-library/Pages/OnePageCheckoutPage";
import BillingAddressForm, {
    BillingAddressFormStyles,
} from "@insite/content-library/Widgets/OnePageCheckout/BillingAddressForm";
import BillToSelectorModal, {
    BillToSelectorModalStyles,
} from "@insite/content-library/Widgets/OnePageCheckout/BillToSelectorModal";
import ShippingAddressForm, {
    ShippingAddressFormStyles,
} from "@insite/content-library/Widgets/OnePageCheckout/ShippingAddressForm";
import ShipToSelectorModal, {
    ShipToSelectorModalStyles,
} from "@insite/content-library/Widgets/OnePageCheckout/ShipToSelectorModal";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps, CheckboxProps } from "@insite/mobius/Checkbox";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import AddressValidationModal from "@rpsc/App/Components/Addresses/AddressValidationModal";
import Notify from "@rpsc/App/Components/Notify";
import AddressValidation, { AddressValidationHandlerResult } from "@rpsc/App/Handlers/XNG/AddressValidation";
import { AddressValidationApiParameter } from "@rpsc/App/Services/AddressValidation";
import { CustomAppState } from "@rpsc/App/Store/Reducers";
import { getSettingsCollection } from "@rpsc/App/Store/Selectors/SettingsSelectors";
import useCheckoutOneTimeAddress from "@rpsc/Overrides/Widgets/OnePageCheckout/useCheckoutOneTimeAddress";
import { delay, parseBoolean, xValidateOnSubmit } from "@rpsc/XDL/Utilities";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks, useStore } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = state.pages.onePageCheckout;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const { value: cart } = cartState;
    // XNG-Change
    const useOneTimeAddress = state.pages.checkoutShipping.useOneTimeAddress;
    return {
        isShippingAddressUpdateRequired: state.pages.onePageCheckout.isShippingAddressUpdateRequired,
        isBillingAddressUpdateRequired: state.pages.onePageCheckout.isBillingAddressUpdateRequired,
        isBillingAddressTheSameAsShipping: state.pages.onePageCheckout.isBillingAddressTheSameAsShipping,
        isSavingShipping: state.pages.onePageCheckout.isSavingShipping,
        cart,
        shipToState: getShipToState(state, cart?.shipToId),
        billToState: getBillToState(state, cart?.billToId),
        currentUserIsGuest: getCurrentUserIsGuest(state),
        countries: getCurrentCountries(state),
        shipTosDataView: cart
            ? getShipTosDataView(state, { billToId: cart.billToId, expand: ["validation"], exclude: ["showAll"] })
            : undefined,
        shipToAddressFields: getAddressFieldsDataView(state).value?.shipToAddressFields,
        billToAddressFields: getAddressFieldsDataView(state).value?.billToAddressFields,
        fulfillmentMethod: getSession(state).fulfillmentMethod,
        pickUpWarehouse: getSession(state).pickUpWarehouse,
        // XNG-Change
        isEditing: (state as CustomAppState).xCheckout.isEditing,
        cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
        allowOneTimeAddress: getSettingsCollection(state).customerSettings.allowOneTimeAddresses,
        useOneTimeAddress,
    };
};

const mapDispatchToProps = {
    loadShipTos,
    loadShipTo,
    loadBillTo,
    setIsBillingAddressTheSameAsShipping,
    setAddressBookOverlayIsOpen,
    setStorePickupOverlayIsOpen,
    saveShipping,
    setPaymentDetailsState,
    // XNG-Change
    setIsEditing: (isEditing: boolean) => ({
        type: "xCheckout/SetIsEditing",
        data: { isEditing },
    }),
    setUseOneTimeAddress,
    resetShippingAddressFormValidation,
    validateAddress: makeHandlerChainAwaitable<Parameters<typeof AddressValidation>[0], AddressValidationHandlerResult>(
        AddressValidation,
    ),
    setShippingAddress: (address: ShipToModel) => ({
        type: "Pages/OnePageCheckout/SetShippingAddress",
        address,
    }),
};

type Props = WidgetProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme>;

export interface OnePageCheckoutShippingSectionStyles {
    loadingWrapper?: InjectableCss;
    container?: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleText?: TypographyPresentationProps;
    titleEditGridItem?: GridItemProps;
    titleEditLink?: LinkPresentationProps;
    pickUpGridItem?: GridItemProps;
    headingGridItem?: GridItemProps;
    heading?: TypographyPresentationProps;
    editLink?: LinkPresentationProps;
    shipToSelectorModal?: ShipToSelectorModalStyles;
    billToSelectorModal?: BillToSelectorModalStyles;
    shipToGridItem?: GridItemProps;
    shippingAddressForm?: ShippingAddressFormStyles;
    addressInfoDisplay?: AddressInfoDisplayStyles;
    useShippingAddressGridItem?: GridItemProps;
    useShippingAddressCheckbox?: CheckboxPresentationProps;
    billToGridItem?: GridItemProps;
    billingAddressForm?: BillingAddressFormStyles;
    saveButtonGridItem?: GridItemProps;
    saveButton?: ButtonPresentationProps;
    // XNG-Change
    title?: TypographyPresentationProps;
    subTitleContainer?: InjectableCss;
    changeLink?: LinkPresentationProps;
    notify?: InjectableCss;
    saveAddress?: InjectableCss;
}

export const onePageCheckoutShippingSectionStyles: OnePageCheckoutShippingSectionStyles = {
    loadingWrapper: {
        css: css`
            display: flex;
            height: 450px;
            justify-content: center;
            align-items: center;
        `,
    },
    container: { gap: 10 },
    titleGridItem: { width: 10 },
    titleText: {
        variant: "h5",
        css: css`
            margin: 0;
        `,
    },
    titleEditGridItem: {
        width: 2,
        css: css`
            justify-content: end;
            align-items: center;
        `,
    },
    pickUpGridItem: {
        width: 12,
        css: css`
            padding-bottom: 24px;
        `,
    },
    headingGridItem: {
        width: 12,
        css: css`
            justify-content: space-between;
            padding: 15px 5px 5px 5px;
        `,
    },
    heading: { weight: 600 },
    shipToGridItem: { width: 12 },
    useShippingAddressGridItem: {
        width: 12,
        css: css`
            padding: 20px 5px;
        `,
    },
    billToGridItem: { width: 12 },
    saveButtonGridItem: { width: 12 },
    saveButton: {
        css: css`
            margin-top: 10px;
        `,
    },
};

const styles = onePageCheckoutShippingSectionStyles;

export const CHECKOUT_BILLING_FORM_ID = "CHECKOUT_BILLING_FORM_ID";
export const CHECKOUT_SHIPPING_FORM_ID = "CHECKOUT_SHIPPING_FORM_ID";

const OnePageCheckoutShippingSection = ({
    isShippingAddressUpdateRequired,
    isBillingAddressUpdateRequired,
    isBillingAddressTheSameAsShipping,
    isSavingShipping,
    cart,
    shipToState,
    billToState,
    currentUserIsGuest,
    countries,
    shipTosDataView,
    shipToAddressFields,
    billToAddressFields,
    fulfillmentMethod,
    pickUpWarehouse,
    theme,
    loadShipTos,
    loadShipTo,
    loadBillTo,
    setIsBillingAddressTheSameAsShipping,
    setAddressBookOverlayIsOpen,
    setStorePickupOverlayIsOpen,
    saveShipping,
    setPaymentDetailsState,
    // XNG-Change
    isEditing,
    setIsEditing,
    cartPageUrl,
    resetShippingAddressFormValidation,
    setUseOneTimeAddress,
    allowOneTimeAddress,
    validateAddress,
    setShippingAddress,
}: Props) => {
    const context = useContext(OnePageCheckoutFormContext);
    // XNG-Change
    // const [isEditing, setIsEditing] = useState(false);
    const [isShipToSelectorModalOpen, setIsShipToSelectorModalOpen] = useState(false);
    const [isBillToSelectorModalOpen, setIsBillToSelectorModalOpen] = useState(false);
    const history = useHistory();
    const store = useStore();
    // <XNG-Change>
    const [openAddressValidationModal, setOpenAddressValidationModal] = useState(false);
    const [addressValidationResult, setAddressValidationResult] = useState({} as AddressValidationHandlerResult);
    const { useOneTimeAddress, toggleOneTimeAddress } = useCheckoutOneTimeAddress({
        setUseOneTimeAddress,
        setIsBillingAddressTheSameAsShipping,
        resetShippingAddressFormValidation,
    });
    const [isLoading, setIsLoading] = useState(false);

    // <XNG-Changes>
    const shipTo = shipToState.value;
    const billTo = billToState.value;

    useEffect(() => {
        // XNG-Change Can't edit billing address.
        if (isShippingAddressUpdateRequired && (parseBoolean(shipTo?.properties.webOnly) || currentUserIsGuest)) {
            // || isBillingAddressUpdateRequired) {
            setIsEditing(true);
        }
    }, [isShippingAddressUpdateRequired, shipTo?.properties.webOnly, currentUserIsGuest]);

    useEffect(() => {
        if (cart && shipTosDataView && !shipTosDataView.value && !shipTosDataView.isLoading) {
            loadShipTos({ billToId: cart.billToId, expand: ["validation"], exclude: ["showAll"] });
        }
    });

    useEffect(() => {
        if (!shipToState.value && !shipToState.isLoading && cart && cart.billToId && cart.shipToId) {
            loadShipTo({ billToId: cart.billToId, shipToId: cart.shipToId });
        }
    }, [shipToState]);

    useEffect(() => {
        if (!billToState.value && !billToState.isLoading && cart && cart.billToId) {
            loadBillTo({ billToId: cart.billToId });
        }
    }, [billToState]);
    // XNG-Change
    useEffect(() => {
        if (shipToState.value?.oneTimeAddress && !useOneTimeAddress && !shipToState.isLoading) {
            toggleOneTimeAddress(true);
        }
    }, [shipToState.value?.oneTimeAddress]);

    if (!cart) {
        return null;
    }

    if (!countries || !shipTo || !billTo) {
        return null;
    }

    const useShippingAddressChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        setIsBillingAddressTheSameAsShipping({ isBillingAddressTheSameAsShipping: value });
        // XNG-Change
        !currentUserIsGuest &&
            setUseOneTimeAddress({
                useOneTimeAddress: false,
            });
    };

    const editShippingAddressClickHandler = () => {
        theme.addressBook?.useOverlay
            ? setAddressBookOverlayIsOpen({ isOpen: true, isBillTo: false })
            : setIsShipToSelectorModalOpen(true);
    };

    const shipToSelectorModalCloseHandler = () => {
        setIsShipToSelectorModalOpen(false);
    };

    const editBillingAddressClickHandler = () => {
        theme.addressBook?.useOverlay
            ? setAddressBookOverlayIsOpen({ isOpen: true, isBillTo: true })
            : setIsBillToSelectorModalOpen(true);
    };

    const billToSelectorModalCloseHandler = () => {
        setIsBillToSelectorModalOpen(false);
    };
    // XNG-Change
    const editPickUpWarehouseClickHandler = () => {
        if (theme.storePickup?.useOverlay) {
            setStorePickupOverlayIsOpen({ isOpen: true });
        } else if (cartPageUrl) {
            history.push(cartPageUrl);
        }
    };

    const titleEditClickHandler = () => {
        if (currentUserIsGuest) {
            setIsEditing(true);
        } else if (fulfillmentMethod === FulfillmentMethod.PickUp) {
            setAddressBookOverlayIsOpen({ isOpen: true, isBillTo: true });
        }
    };

    const saveClickHandler = async (skipValidation = false) => {
        // <XNG-Change> cleanup + loading
        setIsLoading(true);
        setAddressValidationResult({} as AddressValidationHandlerResult);
        // </XNG-Change>
        context.formSubmitAttempted = true;

        const validators: (() => Promise<boolean>)[] = [];
        Object.keys(context.validators).forEach(key => {
            const validator = context.validators[key];
            if (validator) {
                validators.push(validator);
            }
        });
        // XNG-Change Add validation
        const results = await Promise.all(validators.map(v => v()));
        const isAllResultsValid = results.every(o => o);
        const errors = xValidateOnSubmit(CHECKOUT_BILLING_FORM_ID);
        if (fulfillmentMethod === FulfillmentMethod.Ship && !isBillingAddressTheSameAsShipping) {
            errors.push(...xValidateOnSubmit(CHECKOUT_SHIPPING_FORM_ID));
        }
        if (!skipValidation && !errors.length && isAllResultsValid) {
            // instead of useRefs.
            const state = store.getState();
            const shipTo = useOneTimeAddress
                ? state.pages.onePageCheckout.shippingAddressFormState?.address
                : isBillingAddressTheSameAsShipping
                ? state.pages.onePageCheckout.billingAddressFormState?.address
                : state.pages.onePageCheckout.shippingAddressFormState?.address;
            const result = await validateAddress({
                ...((shipTo || {}) as AddressValidationApiParameter),
            });
            if (!result.skipValidation && !result.addressResultDto?.isMatch) {
                // when the validation is on
                setIsLoading(false);
                setAddressValidationResult(result);
                setOpenAddressValidationModal(true);
                return;
            }
        }

        if (!errors.length && isAllResultsValid) {
            setIsLoading(false);
            saveShipping({
                onSuccess: () => {
                    setIsEditing(false);
                    setPaymentDetailsState({ paymentDetailsState: "active" });
                },
            });
        } else {
            setIsLoading(false);
            scrollToFirstFormError();
        }
    };

    return (
        <LoadingOverlay loading={isLoading}>
            <StyledWrapper {...styles.container} data-test-selector="shippingAndBillingSection">
                <StyledWrapper {...styles.titleGridItem}>
                    <Typography as="h2" {...styles.title}>
                        {translate("Addresses")}
                    </Typography>
                    {!isEditing && currentUserIsGuest && (
                        <Link
                            onClick={titleEditClickHandler}
                            {...styles.editLink}
                            aria-label={translate("Edit Addresses")}
                        >
                            {translate("Edit Addresses")}
                        </Link>
                    )}
                </StyledWrapper>
                {fulfillmentMethod === FulfillmentMethod.Ship &&
                    (currentUserIsGuest || shipTo.country?.abbreviation.toLowerCase().startsWith("us")) &&
                    !!siteMessage("Custom_US_Addresses_Note").toString().length && (
                        <StyledWrapper {...styles.titleGridItem}>
                            <Notify type="info" {...styles.notify}>
                                {siteMessage("Custom_US_Addresses_Note")}
                            </Notify>
                        </StyledWrapper>
                    )}

                {fulfillmentMethod === FulfillmentMethod.PickUp && pickUpWarehouse && (
                    <>
                        <StyledWrapper {...styles.pickUpGridItem}>
                            <StyledWrapper {...styles.subTitleContainer}>
                                <Typography as="h2" {...styles.titleText}>
                                    {translate("Pickup Address")}
                                </Typography>

                                <Link
                                    onClick={editPickUpWarehouseClickHandler}
                                    data-test-selector="changeCustomer_pickUpLocation_findLocation"
                                    {...styles.changeLink}
                                    aria-label="changeBranch"
                                >
                                    {/* xng-change:change label */}
                                    {translate("Change Branch")}
                                </Link>
                            </StyledWrapper>
                            <AddressInfoDisplay
                                {...pickUpWarehouse}
                                companyName={pickUpWarehouse.description || pickUpWarehouse.name}
                                extendedStyles={styles.addressInfoDisplay}
                            />
                        </StyledWrapper>
                    </>
                )}
                {/* <StyledWrapper {...styles.titleGridItem}>
                    <Typography as="h2" {...styles.titleText}>
                        {fulfillmentMethod === FulfillmentMethod.Ship
                            ? translate("Shipping")
                            : translate("Billing Address")}
                    </Typography>
                </StyledWrapper> */}
                {/* Bill to */}
                {billToAddressFields && isEditing && currentUserIsGuest ? (
                    <StyledWrapper {...styles.billToGridItem}>
                        <Typography as="h2" {...styles.titleText}>
                            {translate("Billing Address")}
                        </Typography>

                        <BillingAddressForm
                            countries={countries}
                            fieldDisplay={billToAddressFields}
                            extendedStyles={styles.billingAddressForm}
                            {...{ formId: CHECKOUT_BILLING_FORM_ID }}
                        />
                    </StyledWrapper>
                ) : (
                    billTo && (
                        <StyledWrapper {...styles.billToGridItem} data-test-selector="BillingAddress">
                            <StyledWrapper {...styles.subTitleContainer}>
                                <Typography as="h2" {...styles.titleText}>
                                    {translate("Billing Address")}
                                </Typography>
                                {!currentUserIsGuest && (
                                    <Link
                                        {...styles.changeLink}
                                        type="button"
                                        onClick={editBillingAddressClickHandler}
                                        data-test-selector="OnePageCheckout_selectSavedAddress"
                                        aria-label={translate("Select Saved Addresses")}
                                    >
                                        {translate("Select Saved Addresses")}
                                        <VisuallyHidden>{translate("For Billing Address")}</VisuallyHidden>
                                    </Link>
                                )}

                                <BillToSelectorModal
                                    isOpen={isBillToSelectorModalOpen}
                                    onModalClose={billToSelectorModalCloseHandler}
                                    extendedStyles={styles.billToSelectorModal}
                                />
                            </StyledWrapper>
                            <AddressInfoDisplay
                                {...billTo}
                                state={billTo.state ? billTo.state.abbreviation : undefined}
                                country={billTo.country ? billTo.country.abbreviation : undefined}
                                extendedStyles={styles.addressInfoDisplay}
                            />
                        </StyledWrapper>
                    )
                )}

                {isEditing ? (
                    <>
                        {fulfillmentMethod === FulfillmentMethod.Ship && shipToAddressFields && (
                            <StyledWrapper {...styles.shipToGridItem}>
                                <StyledWrapper {...styles.subTitleContainer}>
                                    <Typography as="h2" {...styles.titleText}>
                                        {translate("Shipping Address")}
                                    </Typography>
                                    {!currentUserIsGuest && (
                                        <>
                                            <Link
                                                {...styles.changeLink}
                                                onClick={editShippingAddressClickHandler}
                                                data-test-selector="editShippingAddressButton"
                                                aria-label={translate("Select Saved Addresses")}
                                            >
                                                {translate("Select Saved Addresses")}
                                                <VisuallyHidden>{translate("Shipping Address")}</VisuallyHidden>
                                            </Link>
                                            <ShipToSelectorModal
                                                isOpen={isShipToSelectorModalOpen}
                                                onModalClose={shipToSelectorModalCloseHandler}
                                                extendedStyles={styles.shipToSelectorModal}
                                            />
                                        </>
                                    )}
                                </StyledWrapper>

                                {currentUserIsGuest && (
                                    <StyledWrapper {...styles.useShippingAddressGridItem}>
                                        <Checkbox
                                            {...styles.useShippingAddressCheckbox}
                                            onChange={useShippingAddressChangeHandler}
                                            checked={isBillingAddressTheSameAsShipping}
                                            data-test-selector="useShippingAddressCheckbox"
                                        >
                                            {translate("Use billing Address")}
                                        </Checkbox>
                                    </StyledWrapper>
                                )}

                                {/* XNG-Change */}
                                {!currentUserIsGuest && allowOneTimeAddress && (
                                    <StyledWrapper {...styles.useShippingAddressGridItem}>
                                        <Checkbox
                                            {...styles.useShippingAddressCheckbox}
                                            onChange={(_, checked) => toggleOneTimeAddress(checked, false)}
                                            checked={useOneTimeAddress}
                                            data-test-selector="useShippingAddressCheckbox"
                                        >
                                            {translate("Ship To a One-Time Address")}
                                        </Checkbox>
                                    </StyledWrapper>
                                )}
                                {!isBillingAddressTheSameAsShipping && (
                                    <ShippingAddressForm
                                        countries={countries}
                                        fieldDisplay={shipToAddressFields}
                                        extendedStyles={styles.shippingAddressForm}
                                        {...{ formId: CHECKOUT_SHIPPING_FORM_ID }}
                                    />
                                )}
                            </StyledWrapper>
                        )}
                    </>
                ) : (
                    fulfillmentMethod === FulfillmentMethod.Ship &&
                    shipTo && (
                        <>
                            <StyledWrapper {...styles.shipToGridItem} data-test-selector="ShippingAddress">
                                <StyledWrapper {...styles.subTitleContainer}>
                                    <Typography as="h2" {...styles.titleText}>
                                        {translate("Shipping Address")}
                                    </Typography>
                                    {/* XNG-Change */}
                                    {!currentUserIsGuest && (
                                        <>
                                            <Link
                                                {...styles.changeLink}
                                                onClick={editShippingAddressClickHandler}
                                                data-test-selector="editShippingAddressButton"
                                                aria-label={translate("Select Saved Addresses")}
                                            >
                                                {translate("Select Saved Addresses")}
                                                <VisuallyHidden>{translate("Shipping Address")}</VisuallyHidden>
                                            </Link>
                                            <ShipToSelectorModal
                                                isOpen={isShipToSelectorModalOpen}
                                                onModalClose={shipToSelectorModalCloseHandler}
                                                extendedStyles={styles.shipToSelectorModal}
                                            />
                                        </>
                                    )}
                                </StyledWrapper>

                                {!currentUserIsGuest && allowOneTimeAddress && (
                                    <StyledWrapper {...styles.useShippingAddressGridItem}>
                                        <Checkbox
                                            {...styles.useShippingAddressCheckbox}
                                            onChange={(_, checked) => toggleOneTimeAddress(checked, false)}
                                            checked={useOneTimeAddress}
                                            data-test-selector="useShippingAddressCheckbox"
                                        >
                                            {translate("Ship To a One-Time Address")}
                                        </Checkbox>
                                    </StyledWrapper>
                                )}
                                <AddressInfoDisplay
                                    {...shipTo}
                                    state={shipTo.state ? shipTo.state.abbreviation : undefined}
                                    country={shipTo.country ? shipTo.country.abbreviation : undefined}
                                    extendedStyles={styles.addressInfoDisplay}
                                />
                            </StyledWrapper>
                        </>
                    )
                )}

                {isEditing && (
                    <StyledWrapper {...styles.saveButtonGridItem}>
                        <Button
                            {...styles.saveButton}
                            onClick={() => saveClickHandler()}
                            data-test-selector="saveButton"
                            aria-label={translate("Save Addresses")}
                        >
                            {translate("Save Addresses")}
                        </Button>
                    </StyledWrapper>
                )}
                {/* XNG-Change */}
                {!isEditing && useOneTimeAddress && (
                    <StyledWrapper {...styles.saveButtonGridItem}>
                        <Button
                            {...styles.saveButton}
                            onClick={() => setIsEditing(true)}
                            data-test-selector="EditButton"
                            aria-label={translate("Edit Address")}
                        >
                            {translate("Edit Address")}
                        </Button>
                    </StyledWrapper>
                )}
                {/* XNG-Change Add validation */}
                <AddressValidationModal
                    selector={state => {
                        const shipTo = useOneTimeAddress
                            ? state.pages.onePageCheckout.shippingAddressFormState?.address
                            : isBillingAddressTheSameAsShipping
                            ? state.pages.onePageCheckout.billingAddressFormState?.address
                            : state.pages.onePageCheckout.shippingAddressFormState?.address;

                        return shipTo as ShipToModel;
                    }}
                    isOpen={openAddressValidationModal}
                    setIsOpen={setOpenAddressValidationModal}
                    updateAddress={async address => {
                        setIsBillingAddressTheSameAsShipping({ isBillingAddressTheSameAsShipping: false });
                        await delay(50);
                        setShippingAddress(address);
                    }}
                    onAcceptSuggestion={() => {
                        saveClickHandler(true);
                    }}
                    onEdit={() => setOpenAddressValidationModal(false)}
                    suggestedAddressResult={addressValidationResult}
                    onUseCurrentAddress={() => {
                        saveClickHandler(true);
                    }}
                />
            </StyledWrapper>
        </LoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(OnePageCheckoutShippingSection)),
    definition: {
        displayName: "Shipping Section",
        group: "One Page Checkout",
        allowedContexts: ["OnePageCheckoutPage"],
    },
};

export default widgetModule;
